import { useTranslation } from "react-i18next";
import {
  PUBLISH_ERROR_CATEGORY_INFO,
  PUBLISH_ERROR_SOURCE_LABEL,
  isPublishErrorCategory,
  isPublishErrorSource,
} from "@content-os/config";

export interface PublishFailureLike {
  errorMessage?: string | null;
  errorCategory?: string | null;
  errorSource?: string | null;
  platformError?: string | null;
}

/** True when there is anything failure-related worth showing. */
export function hasPublishFailure(f: PublishFailureLike): boolean {
  return Boolean(f.errorMessage || f.errorCategory || f.errorSource || f.platformError);
}

/**
 * A failed post's reason: Zernio's category (what went wrong), who has to act,
 * the human-readable message, and the platform's own error when it differs.
 */
export function PublishFailureDetails({
  failure,
  compact = false,
}: {
  failure: PublishFailureLike;
  compact?: boolean;
}) {
  const { t } = useTranslation("calendar");
  if (!hasPublishFailure(failure)) return null;

  const category = isPublishErrorCategory(failure.errorCategory) ? failure.errorCategory : null;
  const source = isPublishErrorSource(failure.errorSource) ? failure.errorSource : null;
  const categoryLabel = category
    ? t(`publishFailure.category.${category}`, { defaultValue: PUBLISH_ERROR_CATEGORY_INFO[category].label })
    : failure.errorCategory ?? null;
  const action = category
    ? t(`publishFailure.action.${category}`, { defaultValue: PUBLISH_ERROR_CATEGORY_INFO[category].action })
    : null;
  const who = source
    ? t(`publishFailure.whoActs.${source}`, { defaultValue: PUBLISH_ERROR_SOURCE_LABEL[source] })
    : null;
  const platformError =
    failure.platformError && failure.platformError !== failure.errorMessage ? failure.platformError : null;

  return (
    <div
      className={`rounded-md bg-red-500/10 text-red-400 ${compact ? "p-1.5 text-xs" : "p-2 text-sm"} space-y-1`}
      data-testid="publish-failure"
    >
      {(categoryLabel || who) && (
        <div className="font-medium">
          {categoryLabel}
          {categoryLabel && who ? " · " : ""}
          {who}
        </div>
      )}
      {failure.errorMessage && <div className="whitespace-pre-wrap break-words">{failure.errorMessage}</div>}
      {!compact && action && <div className="text-red-300/80">{action}</div>}
      {!compact && platformError && (
        <div className="text-xs text-red-300/80 break-words">
          {t("publishFailure.platformError")}: {platformError}
        </div>
      )}
    </div>
  );
}
